/* eslint no-shadow: ["error", { "allow": ["state"] }] */
import api from '@/api';

export const namespaced = true;

export const state = {
  properties: [],
  propertiesLoading: false,
  isPropertyEdit: false,
  currentProperty: {},
  propertyProjectId: null,
  propertyRolesOptions: [],
  propertyRolesLoading: false,
  submitting: false,
  latestLocalityId: '',
  searchResults: [],
  serverError: '',
};

export const actions = {
  async resetProperties({ commit }) {
    commit('SET_PROPERTIES', []);
    commit('SET_PROPERTY_PROJECT_ID', 0);
  },
  async fetchProperties({ commit, state }, projectId) {
    // only fetch if not already loaded
    if (state.propertyProjectId !== projectId) {
      commit('SET_PROPERTIES_LOADING', true);
      await api.get(`/project/${projectId}/properties`)
        .then((response) => {
          commit('SET_PROPERTIES', response.data);
          commit('SET_PROPERTIES_LOADING', false);
          commit('SET_PROPERTY_PROJECT_ID', projectId);
        })
        .catch(() => {
          commit('SET_PROPERTIES_LOADING', false);
        });
    }
  },
  async forceFetchProperties({ commit }, projectId) {
    commit('SET_PROPERTIES_LOADING', true);
    await api.get(`/project/${projectId}/properties`)
      .then((response) => {
        commit('SET_PROPERTIES', response.data);
        commit('SET_PROPERTIES_LOADING', false);
        commit('SET_PROPERTY_PROJECT_ID', projectId);
      })
      .catch(() => {
        commit('SET_PROPERTIES_LOADING', false);
      });
  },
  async deleteProperty({ commit, dispatch, rootState }, { propertyId, submittedCallback }) {
    commit('START_SUBMITTING');
    await api.delete(`/property/delete/${propertyId}`)
      .then(() => {
        commit('SET_SERVER_ERROR', '');
        commit('STOP_SUBMITTING');
        const { id } = rootState.CurrentProject.currentProject;
        dispatch('CurrentProject/fetchProjectDeleteMessage', { projectId: id }, { root: true });
        dispatch('CurrentProject/refreshLatestEvents', { projectId: id }, { root: true });
        submittedCallback();
      })
      .catch((error) => {
        commit('SET_SERVER_ERROR', error.response.data);
        commit('STOP_SUBMITTING');
      });
  },
  async fetchProperty({ commit, getters, dispatch }, { projectId, id }) {
    commit('SET_PROPERTY_EDIT', false);
    const property = getters.getProperty(id);
    if (property) {
      commit('SET_CURRENT_PROPERTY', property);
    } else {
      // if instrument wasn't already loaded, load all instruments and try again
      // if not found this time, it's ok to set a nil instrument in state
      await dispatch('fetchProperties', projectId);
      const propertySecondAttempt = getters.getProperty(id);
      commit('SET_CURRENT_PROPERTY', propertySecondAttempt);
    }
  },
  async fetchPropertiesFromServer({ commit, dispatch }) {
    commit('SET_PROPERTY_PROJECT_ID', null);
    await dispatch('fetchProperties');
  },
  async refetchCurrentProperty({ commit, state }) {
    commit('SET_PROPERTIES_LOADING', true);
    await api.get(`/property/${state.currentProperty.id}`)
      .then((response) => {
        commit('SET_CURRENT_PROPERTY', response.data);
        commit('SET_PROPERTIES_LOADING', false);
      })
      .catch(() => {
        commit('SET_PROPERTIES_LOADING', false);
      });
  },
  async refetchDestinationProperty({ commit }, destinationPropertyId) {
    commit('SET_PROPERTIES_LOADING', true);
    await api.get(`/property/${destinationPropertyId}`)
      .then((response) => {
        commit('SET_PROPERTY', response.data);
        commit('SET_PROPERTIES_LOADING', false);
      })
      .catch(() => {
        commit('SET_PROPERTIES_LOADING', false);
      });
  },
  async fetchPropertyRoles({ commit, state }) {
    // only fetch if not already loaded
    if (state.propertyRolesOptions.length === 0) {
      commit('SET_PROPERTY_ROLES_LOADING', true);
      await api.get('/property/contactroles')
        .then((response) => {
          commit('SET_PROPERTY_ROLES', response.data);
          commit('SET_PROPERTY_ROLES_LOADING', false);
        })
        .catch(() => {
          commit('SET_PROPERTY_ROLES_LOADING', false);
        });
    }
  },
  async createEntity({ commit, dispatch, rootState }, { entity, submittedCallback }) {
    api.post('/property/create', entity)
      .then((response) => {
        api.get(`/property/${response.data}`)
          .then((getResponse) => {
            commit('ADD_NEW_PROPERTY', getResponse.data);
            commit('SET_SERVER_ERROR', '');
            commit('STOP_SUBMITTING');
            if (rootState.CurrentProject.currentProject) {
              const { id } = rootState.CurrentProject.currentProject;
              dispatch('CurrentProject/clearCurrentProject', null, { root: true });
              dispatch('CurrentProject/fetch',
                { projectId: id },
                { root: true });
            }
            if (submittedCallback) submittedCallback(getResponse.data.id);
          })
          .catch((error) => {
            commit('SET_SERVER_ERROR', error.getResponse.data);
            commit('STOP_SUBMITTING');
          });
      })
      .catch((error) => {
        commit('SET_SERVER_ERROR', error.response.data);
        commit('STOP_SUBMITTING');
      });
  },
  async updateEntity({ commit, dispatch, rootState }, { entity, submittedCallback }) {
    await api.put('/property/update', entity)
      .then((response) => {
        commit('SET_CURRENT_PROPERTY', response.data);
        commit('SET_PROPERTY_EDIT', false);
        commit('SET_SERVER_ERROR', '');
        commit('STOP_SUBMITTING');
        if (rootState.CurrentProject.currentProject) {
          const { id } = rootState.CurrentProject.currentProject;
          dispatch('CurrentProject/clearCurrentProject', null, { root: true });
          dispatch('CurrentProject/fetch',
            { projectId: id },
            { root: true });
        }
        if (submittedCallback) submittedCallback();
      }).catch((error) => {
        commit('SET_SERVER_ERROR', error.response.data);
        commit('STOP_SUBMITTING');
      });
  },
  async updateDocumentFolder({ commit }, { id, folderId }) {
    commit('START_SUBMITTING');
    await api.patch(`/property/${id}/updateDocumentFolder`, { folderId })
      .then((response) => {
        commit('SET_DOCUMENT_FOLDER', response.data);
        commit('SET_SERVER_ERROR', '');
        commit('STOP_SUBMITTING');
      })
      .catch((error) => {
        commit('SET_SERVER_ERROR', error.response.data);
        commit('STOP_SUBMITTING');
      });
  },
  switchToEdit({ commit }) {
    commit('SET_PROPERTY_EDIT', true);
  },
  stopEditing({ commit }) {
    commit('SET_PROPERTY_EDIT', false);
  },
  async startSubmitting({ commit }) {
    commit('START_SUBMITTING');
  },
  async stopSubmitting({ commit }) {
    commit('STOP_SUBMITTING');
  },
  async fetchLatestPropertyLocality({ commit }, projectId) {
    await api.get(`/project/${projectId}/latestPropertyLocality`)
      .then(response => commit('SET_LATEST_LOCALITY', response.data));
  },
  async search({ commit }) {
    await api.get('/property/search')
      .then((response) => {
        commit('SET_SEARCH_RESULTS', response.data);
      });
  },
};

export const mutations = {
  SET_PROPERTY_EDIT(state, isPropertyEdit) {
    state.isPropertyEdit = isPropertyEdit;
  },
  SET_PROPERTIES_LOADING(state, loading) {
    state.propertiesLoading = loading;
  },
  SET_PROPERTY_PROJECT_ID(state, projectId) {
    state.propertyProjectId = projectId;
  },
  SET_PROPERTIES(state, properties) {
    state.properties = properties;
  },
  ADD_NEW_PROPERTY(state, newObj) {
    state.properties.push(newObj);
  },
  SET_CURRENT_PROPERTY(state, property) {
    state.currentProperty = property;
    if (property) {
      // replace the property object in the array of properties
      const currentPropertyIndex = state.properties.findIndex(prop => prop.id === property.id);
      if (currentPropertyIndex >= 0) {
        state.properties.splice(currentPropertyIndex, 1, property);
      } else {
        state.properties.splice(0, 0, property);
      }
    }
  },
  SET_PROPERTY(state, property) {
    if (property) {
      // replace the property object in the array of properties
      const currentPropertyIndex = state.properties.findIndex(prop => prop.id === property.id);
      if (currentPropertyIndex >= 0) {
        state.properties.splice(currentPropertyIndex, 1, property);
      } else {
        state.properties.splice(0, 0, property);
      }
    }
  },
  SET_PROPERTY_ROLES_LOADING(state, propertyRolesLoading) {
    state.propertyRolesLoading = propertyRolesLoading;
  },
  SET_PROPERTY_ROLES(state, propertyRolesOptions) {
    state.propertyRolesOptions = propertyRolesOptions;
  },
  START_SUBMITTING(state) {
    state.submitting = true;
  },
  STOP_SUBMITTING(state) {
    state.submitting = false;
  },
  SET_LATEST_LOCALITY(state, localityId) {
    state.latestLocalityId = localityId;
  },
  SET_DOCUMENT_FOLDER(state, folderId) {
    state.currentProperty.documentFolderId = folderId;
  },
  SET_SEARCH_RESULTS(state, results) {
    state.searchResults = results;
  },
  SET_SERVER_ERROR(state, serverError) {
    state.serverError = serverError;
  },
};

export const getters = {
  orderedProperties: state => state.properties
    .slice(0)
    .sort((a, b) => a.statusId - b.statusId
      || b.calculatedAcres - a.calculatedAcres
      || b.id - a.id),
  getOrderedCurrentProperties: state => state.properties
    .slice(0)
    .filter(prop => prop.statusId === 1)
    .sort((a, b) => b.calculatedAcres - a.calculatedAcres
      || b.id - a.id),
  getDefaultProperty: (state) => {
    if (state.properties.length > 0) {
      return getters.orderedProperties(state)[0];
    }
    return null;
  },
  propertyCount: (state) => {
    if (state.properties) {
      return state.properties.length;
    }
    return 0;
  },
  getProperty: state => (id) => {
    if (id) return state.properties.find(a => a.id === Number(id));
    return null;
  },
};
